import React, { useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';

import { useData } from '../_libs/hooks/useData';
import DataColumn from '../atoms/DataColumn';
import DataBarGraph from '../molecules/data/DataBarGraph';

interface NPIsType {
  familyMedicine: number;
  generalPractice: number;
  internalMedicine: number;
  pediatricians: number;
  psychiatryNeurology: number;
  psychologist: number;
}

const fetchNPIsForZipCode = (
  zipCode,
  radius = 10,
  setNPIs: (NPIs: any) => void,
) => {
  axios
    .get(`/fetch_npis_for_zipcode/${zipCode}/${radius}/`)
    .then(
      (res: AxiosResponse) => {
        const data = res.data;
        if (data.status === 'OK') {
          setNPIs(data.NPIs);
        } else {
          setNPIs(null);
          console.error(data);
        }
      },
      (err: any) => {
        setNPIs(null);
        console.error(err);
      },
    );
};

function roundUp(base: number, increment = 25): number {
  const _base = base / increment;
  if (_base <= 1) { return base; }
  else { return Math.ceil(_base) * increment; }
}

const NPIsGraph: React.FC<{
  geoId: string;
  radius: number;
  zipCode: string;
}> = ({ radius, zipCode }) => {
  const [NPIs, setNPIs] = useState<NPIsType>(null);
  const [NPIsMax, setNPIsMax] = useState<number>(null);
  const { reportData, setReportData } = useData();

  useEffect(() => {
    fetchNPIsForZipCode(zipCode, radius, setNPIs);
  }, [zipCode, radius]);

  useEffect(() => {
    if (NPIs) {
      const _NPIsMax = roundUp(
        _.max([
          NPIs.familyMedicine, 
          NPIs.generalPractice, 
          NPIs.internalMedicine,
          NPIs.pediatricians,
          NPIs.psychiatryNeurology,
          NPIs.psychologist,
        ])
      );
      const _reportData = _.assign(reportData, {
        NPIs,
        NPIsMax: _NPIsMax,
      });
      setNPIsMax(_NPIsMax);
      setReportData(_reportData);
    }
  }, [NPIs]);
  
  return (
    NPIs &&
      <DataColumn>
        <h4>Healthcare Providers within {radius} Miles of {zipCode}</h4>
        <DataBarGraph
          label={'Family Medicine'}
          percent={NPIs.familyMedicine / NPIsMax * 100}
          value={NPIs.familyMedicine}
        />
        <DataBarGraph
          label="General Practice"
          percent={NPIs.generalPractice / NPIsMax * 100}
          value={NPIs.generalPractice}
        />
        <DataBarGraph
          label="Internal Medicine"
          percent={NPIs.internalMedicine / NPIsMax * 100}
          value={NPIs.internalMedicine}
        />
        <DataBarGraph
          label="Pediatricians"
          percent={NPIs.pediatricians / NPIsMax * 100}
          value={NPIs.pediatricians}
        />
        <DataBarGraph
          label="Psychiatrists/Neurologists"
          percent={NPIs.psychiatryNeurology / NPIsMax * 100}
          value={NPIs.psychiatryNeurology}
        />
        <DataBarGraph
          label="Psychologists"
          percent={NPIs.psychologist / NPIsMax * 100}
          value={NPIs.psychologist}
        />
      </DataColumn>
  );
};

export default NPIsGraph;
